import { apiClient } from "./authService";
import type { User } from "@/types/auth";

export interface CreateUserRequest {
  email: string;
  nombre: string;
  apellido: string;
  activo?: boolean;
}

export interface UpdateUserRequest {
  nombre?: string;
  apellido?: string;
  activo?: boolean;
}

/**
 * Servicio para gestión de usuarios
 * Utiliza apiClient con interceptores automáticos para autenticación
 * Todas las requests incluyen automáticamente el token JWT
 */
class UserService {
  /**
   * Obtener todos los usuarios con paginación
   */
  async getAllUsers(
    page: number = 1,
    limit: number = 10,
    search?: string,
    activo?: boolean,
  ): Promise<{ users: User[]; total: number; page: number; limit: number }> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      if (search) {
        params.append("search", search);
      }

      if (activo !== undefined) {
        params.append("activo", activo.toString());
      }

      const response = await apiClient.get<{
        users: User[];
        total: number;
        page: number;
        limit: number;
      }>(`/users?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo usuarios:", error);
      throw new Error("No se pudieron cargar los usuarios");
    }
  }

  /**
   * Obtener usuario por ID
   */
  async getUserById(id: string): Promise<User> {
    try {
      const response = await apiClient.get<User>(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo usuario:", error);
      throw new Error("No se pudo cargar el usuario");
    }
  }

  /**
   * Crear nuevo usuario
   */
  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response = await apiClient.post<User>("/users", userData);
      return response.data;
    } catch (error) {
      console.error("Error creando usuario:", error);
      throw new Error("No se pudo crear el usuario");
    }
  }

  /**
   * Actualizar usuario existente
   */
  async updateUser(id: string, userData: UpdateUserRequest): Promise<User> {
    try {
      const response = await apiClient.put<User>(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      console.error("Error actualizando usuario:", error);
      throw new Error("No se pudo actualizar el usuario");
    }
  }

  /**
   * Eliminar usuario
   */
  async deleteUser(id: string): Promise<void> {
    try {
      await apiClient.delete(`/users/${id}`);
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      throw new Error("No se pudo eliminar el usuario");
    }
  }

  /**
   * Activar/Desactivar usuario
   */
  async toggleUserStatus(id: string, activo: boolean): Promise<User> {
    try {
      const response = await apiClient.patch<User>(`/users/${id}/status`, {
        activo,
      });
      return response.data;
    } catch (error) {
      console.error("Error cambiando estado del usuario:", error);
      throw new Error("No se pudo cambiar el estado del usuario");
    }
  }
}

// Exportar instancia única del servicio
export const userService = new UserService();
